import React from 'react';
import { useGetList } from 'react-admin';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import type { FacetGroup } from '@src/facets';

export interface ItemAdjustedPricesFilterValues {
  parentSku: string;
  skuSubcode: string;
  color: string;
  yadjustedCode: string;
  sizeCode: string;
  department: string;
  category: string;
}

export const EMPTY_FILTERS: ItemAdjustedPricesFilterValues = {
  parentSku: '',
  skuSubcode: '',
  color: '',
  yadjustedCode: '',
  sizeCode: '',
  department: '',
  category: '',
};

interface ItemAdjustedPricesFiltersProps {
  filters: ItemAdjustedPricesFilterValues;
  onChange: (filters: ItemAdjustedPricesFilterValues) => void;
}

const ItemAdjustedPricesFilters = ({ filters, onChange }: ItemAdjustedPricesFiltersProps) => {
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const departmentFacet = facetGroups.find(fg => fg.tag === 'department');
  const categoryFacet = facetGroups.find(fg => fg.tag === 'category');

  const departmentChoices = departmentFacet?.values.map(v => v.name) ?? [];
  const categoryChoices = categoryFacet?.values.map(v => v.name) ?? [];

  const handleTextChange = (field: keyof ItemAdjustedPricesFilterValues) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...filters, [field]: e.target.value });
    };

  const handleSelectChange = (field: keyof ItemAdjustedPricesFilterValues) =>
    (e: { target: { value: string } }) => {
      onChange({ ...filters, [field]: e.target.value });
    };

  return (
    <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
      <TextField
        label="Parent SKU"
        value={filters.parentSku}
        onChange={handleTextChange('parentSku')}
        size="small"
        sx={{ width: 140 }}
      />
      <TextField
        label="SKU Subcode"
        value={filters.skuSubcode}
        onChange={handleTextChange('skuSubcode')}
        size="small"
        sx={{ width: 140 }}
      />
      <TextField
        label="Color"
        value={filters.color}
        onChange={handleTextChange('color')}
        size="small"
        sx={{ width: 120 }}
      />
      <TextField
        label="Y-Adjusted Code"
        value={filters.yadjustedCode}
        onChange={handleTextChange('yadjustedCode')}
        size="small"
        sx={{ width: 150 }}
      />
      <TextField
        label="Size Code"
        value={filters.sizeCode}
        onChange={handleTextChange('sizeCode')}
        size="small"
        sx={{ width: 120 }}
      />
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Department</InputLabel>
        <Select
          value={filters.department}
          onChange={handleSelectChange('department')}
          label="Department"
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {departmentChoices.map(dept => (
            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ width: 150 }}>
        <InputLabel>Category</InputLabel>
        <Select
          value={filters.category}
          onChange={handleSelectChange('category')}
          label="Category"
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {categoryChoices.map(cat => (
            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ItemAdjustedPricesFilters;
